import { type FC, useRef, useEffect } from "react";
import { Box, Stack, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { tokens } from "src/locales/tokens";
import { useTranslation } from "react-i18next";
import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    text: z
      .string()
      .max(1000, t(tokens.general.validators.maxLength1000) as string)
      .min(1, t(tokens.general.validators.required) as string),
  });

// Define the Values type using Zod inference
type Values = z.infer<ReturnType<typeof createValidationSchema>>;

interface TextFormProps {
  value?: string;
  placeholder?: string;
  isMultiline?: boolean;
  isSubmitting?: boolean;
  onSubmit: (text: string) => Promise<any>;
}

export const TextForm: FC<TextFormProps> = (props) => {
  const { onSubmit, value, placeholder, isSubmitting, isMultiline } = props;
  const { t } = useTranslation();
  const textRef = useRef<HTMLInputElement>(null);

  // Create the validation schema using the function
  const validationSchema = createValidationSchema(t);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    defaultValues: {
      text: value ?? "",
    },
    resolver: zodResolver(validationSchema),
  });

  // Reset form when value prop changes
  useEffect(() => {
    reset({ text: value ?? "" });
  }, [value, reset]);

  return (
    <form onSubmit={handleSubmit(async (data) => await onSubmit(data.text))}>
      <Stack spacing={3}>
        <Controller
          name="text"
          control={control}
          render={({ field }) => (
            <TextField
              inputRef={textRef}
              error={!!errors.text}
              helperText={errors.text?.message}
              fullWidth
              autoFocus
              multiline={isMultiline}
              minRows={3}
              sx={{
                "& input": {
                  p: 2,
                },
                "& textarea": {
                  p: 2,
                },
              }}
              InputProps={{
                sx: {
                  p: 0,
                },
              }}
              placeholder={placeholder ?? ""}
              {...field}
            />
          )}
        />

        <Box>
          <LoadingButton
            type="submit"
            size="large"
            loading={isSubmitting}
            variant="contained"
            sx={{
              minWidth: 200,
              height: 56,
            }}
          >
            {t(tokens.general.buttons.next)}
          </LoadingButton>
        </Box>
      </Stack>
    </form>
  );
};
